import React, { useMemo } from "react";
import { ImageView, ActivityIndicator, AssetType } from "@thenounproject/lingo-core";
import { useInView } from "react-intersection-observer";
import { useTheme } from "styled-components";
import { ThumbnailProps } from "./types";
import ThumbnailWrapper from "./ThumbnailWrapper";
import { getAssetError } from "@helpers/getAssetError";
import { getAssetIcon } from "@helpers/getAssetIcon";
import { getThumbnailSize } from "@components/kits/CanvasDrag/helpers";
import getAssetBackground from "@src/helpers/getAssetBackground";
import VideoThumbnail from "./VideoThumbnail";
import AnimatedThumbnail from "./AnimatedThumbnail";

function ImageThumbnail({ asset, item }: ThumbnailProps) {
  const theme = useTheme();
  const { inView, ref } = useInView({ triggerOnce: true });
  const assetError = getAssetError(asset);
  const assetIcon = getAssetIcon(asset);
  const autoplay = item?.displayProperties?.autoplay;
  const isGif = asset.type === AssetType.gif;
  const isLottie = asset.type === AssetType.lottie;
  const isVideo = AssetType.videoTypes.has(asset.type);
  const size = item?.displayProperties?.size || 3;
  const thumbnailSize = getThumbnailSize(size);
  const backgroundColor = useMemo(() => {
    return getAssetBackground(asset, theme);
  }, [theme, asset]);
  const hasPadding = asset.meta.backgroundColor !== "clear";

  const aspectRatio = useMemo(() => {
    const parseDimensions = (dims?: string) => {
      if (!dims) return undefined;
      const [w, h] = dims.split("x").map(Number);
      return w && h ? w / h : undefined;
    };

    const fromPreview = parseDimensions(asset.meta?.preview?.dimensions);
    if (fromPreview) return fromPreview;

    if (asset.width && asset.height) return asset.width / asset.height;

    return parseDimensions(asset.dimensions);
  }, [asset.width, asset.height, asset.dimensions, asset.meta?.preview?.dimensions]);

  function renderThumbnail() {
    if (asset.meta.assetProcessing === "error" || assetError) {
      return (
        <ImageView width="100%" height="100%" icon={"info.error"} iconSize={24} iconFill="error" />
      );
    }

    if (inView && asset.meta.assetProcessing === "complete") {
      if (isVideo) {
        return (
          <VideoThumbnail
            asset={asset}
            poster={asset.thumbnails[thumbnailSize]}
            autoplay={autoplay}
          />
        );
      }

      if (isGif || isLottie) {
        return (
          <AnimatedThumbnail asset={asset} thumbnailSize={thumbnailSize} autoplay={autoplay} />
        );
      }
      return (
        <ImageView
          width="100%"
          height="100%"
          src={asset.thumbnails[thumbnailSize]}
          icon={assetIcon}
          iconSize={32}
          style={{ display: "flex", alignItems: "center", justifyContent: "center" }}
        />
      );
    }

    return <ActivityIndicator size="small" color="gray" center />;
  }

  return (
    <ThumbnailWrapper
      p={hasPadding ? "8px" : undefined}
      aspectRatio={aspectRatio}
      ref={ref}
      {...backgroundColor}>
      {renderThumbnail()}
    </ThumbnailWrapper>
  );
}

export default React.memo(ImageThumbnail);
